import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

type MetricTone =
  | "neutral"
  | "positive"
  | "negative"
  | "warning"
  | "critical"
  | "info";

export interface KeyValueItem {
  key: string;
  label: string;
  value: ReactNode | null | undefined;
  unit?: ReactNode;
  context?: ReactNode;
  tone?: MetricTone;
  monospace?: boolean;
}

export interface KeyValueListProps {
  items: readonly KeyValueItem[];
  ariaLabel?: string;
  columns?: 1 | 2;
  emptyValue?: ReactNode;
  className?: string;
  itemClassName?: string;
}

const valueToneClasses: Record<MetricTone, string> = {
  neutral: "text-fg",
  positive: "text-posGreen",
  negative: "text-negRed",
  warning: "text-warnOrange",
  critical: "text-negRed",
  info: "text-accent",
};

export function KeyValueList({
  items,
  ariaLabel,
  columns = 1,
  emptyValue = "—",
  className,
  itemClassName,
}: KeyValueListProps) {
  return (
    <dl
      aria-label={ariaLabel}
      className={cn(
        "grid min-w-0 grid-cols-1 divide-y divide-border",
        columns === 2 && "min-[480px]:grid-cols-2 min-[480px]:gap-x-4 min-[480px]:divide-y-0",
        className
      )}
    >
      {items.map((item) => {
        const hasValue = item.value !== null && item.value !== undefined;
        return (
          <div
            key={item.key}
            className={cn(
              "grid min-w-0 grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-x-3 py-3 first:pt-0 last:pb-0",
              itemClassName
            )}
          >
            <dt className="min-w-0 break-words text-sm leading-5 text-muted">
              {item.label}
            </dt>
            <dd
              className={cn(
                "min-w-0 break-words text-right text-sm font-medium leading-5 tabular-nums",
                valueToneClasses[item.tone ?? "neutral"],
                item.monospace && "font-mono"
              )}
            >
              {hasValue ? item.value : emptyValue}
              {hasValue && item.unit ? (
                <span className="ml-1 font-normal text-muted">{item.unit}</span>
              ) : null}
              {item.context ? (
                <span className="mt-0.5 block font-normal leading-5 text-muted">
                  {item.context}
                </span>
              ) : null}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
